import { BasicDoc, Hit, StateResultsProvided } from "react-instantsearch-core";
import { Configure, connectStateResults } from "react-instantsearch-dom";
import {
  TrackingEventName,
  trackOfferDetailsEvent,
  trackOfferListEvent,
} from "../../utils/event-tracking";
import { useEffect, useState } from "react";

import { Box } from "@vinomofo/components";
import { CurrentFilter } from "../refinement-menu/CurrentFilter";
import { Media } from "gatsby-plugin-fresnel";
import { OfferCard } from "../offer-card";
import { Pagination } from "./Pagination";
import { RefinementMenu } from "../refinement-menu";
import { VinoDataOffer } from "../../types/vino-data-types";
import { debounce } from "../../utils/debounce";
import { offerCardPropsFromVinoDataOffer } from "../../transformers/vino-data/offer-card";
import { skeletonOffer } from "./skeleton-offer";
import styled from "styled-components";
import { useInstantSearch } from "../../hooks/use-instantsearch";
import { useLocation } from "@reach/router";
import useAuth from "../../hooks/use-auth";
import { EventType } from "../../types";

const HITS_PER_PAGE = 18;

export const OfferGrid = styled(Box)<{
  columns: number;
  neaten: boolean;
  isMyOffer: boolean;
}>`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: ${({ neaten }) => (!neaten ? "60px 10px" : "30px 8px")};

  ${({ neaten }) =>
    neaten && {
      paddingTop: "1.5em",
    }}

  @media ${({ theme }) => theme.mq.min.tablet} {
    grid-template-columns: repeat(
      ${({ columns = 3 }) => columns},
      minmax(23.8%, 1fr)
    );
    grid-gap: ${({ neaten, isMyOffer }) =>
      isMyOffer ? "1.8em 1em" : !neaten ? "3.8em 1em" : "1em"};

    ${({ neaten }) =>
      neaten && {
        paddingTop: "1.5em",
      }}
  }
`;

export const NoItem = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const StyledBox = styled(Box)`
  @media (min-width: 1200px) {
    min-height: 500px;
  }
`;

export type OfferHit = VinoDataOffer & Hit;

export const Offers = ({
  offers,
  columns,
  skeleton,
  tidyUp,
  isPreferredPage,
  moreBtnLink,
  isRecommended,
  isMyOffer = false,
  engine,
  queryID,
}: {
  offers: Array<OfferHit> | Array<BasicDoc>;
  skeleton?: boolean;
  columns?: number;
  tidyUp?: boolean;
  isPreferredPage?: boolean;
  moreBtnLink?: string;
  isRecommended?: boolean;
  isMyOffer?: boolean;
  engine?: string;
  queryID?: string;
}): JSX.Element => {
  const location = useLocation();

  return (
    <OfferGrid
      mb={isMyOffer ? 12 : !tidyUp ? 20 : 0}
      columns={columns}
      neaten={tidyUp}
      marginBottom={isPreferredPage ? "24px" : ""}
      marginTop={isMyOffer ? "12px" : ""}
      paddingTop={isPreferredPage ? "1.5em !important" : "inherit"}
      padding={isMyOffer ? "0 12px" : "0 8px"}
      isMyOffer={isMyOffer}
    >
      {offers
        .filter((offer) => offer.id)
        .map((offer, index) => {
          const trackEvent = (eventName: TrackingEventName) =>
            trackOfferDetailsEvent(
              eventName,
              {
                ...offer,
                slot: index + 1,
                url: `${location.href}${offer.route.slug}`,
                eventType: EventType.Click,
                product_id: offers?.objectID || offers?.id,
                engine,
              },
              "",
              queryID
            );

          const transformedOfferData = offerCardPropsFromVinoDataOffer({
            ...(isMyOffer ? offer.offerJson : offer),
            baseUrl: location.href,
          });

          return (
            <OfferCard
              key={index}
              {...transformedOfferData}
              offerJson={isMyOffer ? offer.offerJson : offer}
              trackEvent={!skeleton && !isMyOffer ? trackEvent : null}
              skeleton={skeleton}
              height="100%"
              variant="responsive"
              borderRadius={
                transformedOfferData.urgencyMessage ? ["0 0 5px 5px"] : 5
              }
              borderWidth={isPreferredPage ? "1px" : "none"}
              borderColor={isPreferredPage ? "background" : "none"}
              borderStyle={isPreferredPage ? "solid" : "none"}
              backgroundColor={isPreferredPage ? "white" : "transparent"}
              boxShadow={!tidyUp ? "rgba(0, 0, 0, 0.15) 0 5px 15px 0" : "none"}
              moreBtnLink={moreBtnLink && index === 3 ? moreBtnLink : undefined}
              isRecommended={isRecommended}
              columns={columns}
              isMyOffer={isMyOffer}
              queryID={queryID}
            />
          );
        })}
    </OfferGrid>
  );
};

const trackProductListViewed = debounce((hits, nbHits) => {
  trackOfferListEvent("Product List Viewed", hits, nbHits);
}, 500);

export const skeletonList = (count: number): Array<OfferHit> => {
  return Array.from(Array(count), (_, i) => ({
    ...skeletonOffer,
    id: `skeleton-hit-${i}`,
  })) as Array<OfferHit>;
};

export interface ProductCollectionProps extends StateResultsProvided {
  filters?: string;
  facetFilters?: string;
  numericFilters?: string;
  limit?: number;
  showPagination?: boolean;
  showRefinements?: boolean;
  columns?: number;
  isCartPage?: boolean;
  isPreferredPage?: boolean;
  query?: string;
  showMoreLink?: string;
}

export const ProductCollection = connectStateResults<ProductCollectionProps>(
  (props: ProductCollectionProps): JSX.Element => {
    const {
      showRefinements,
      showPagination,
      columns,
      filters,
      limit,
      searchResults,
      isSearchStalled,
      query,
      showMoreLink,
      searchState,
      searching,
    } = props;
    const location = useLocation();
    const {
      onSearchStateChange,
      carryOverFilter,
      tierFilter,
    } = useInstantSearch();
    const [mounted, setMounted] = useState(false);
    const { user } = useAuth();

    const params = new URLSearchParams(location.search);
    const queryParam = params.get("query") || "";
    const isWinesOrSpiritsPath =
      location.pathname.includes("/wines") ||
      location.pathname.includes("/spirits");
    const isBlackMarket = filters === "blackMarket:true";

    const filtersData: string | undefined = (() => {
      if (isWinesOrSpiritsPath && queryParam !== "") return "";
      if (isBlackMarket) return "secret:true";
      return filters;
    })();

    const combinedFilters =
      filtersData && tierFilter
        ? `(${filtersData}) AND (${tierFilter})`
        : filtersData || tierFilter || "";

    useEffect(() => {
      setMounted(true);
    }, []);

    useEffect(() => {
      if (searchResults && searchResults.hits[0]) {
        console.log("searchResults", searchResults);
        if (searchResults?.queryID) {
          localStorage.setItem("queryID", searchResults?.queryID);
        }
        if (location.pathname === "/") {
          trackOfferListEvent(
            "Product List Viewed",
            searchResults.hits,
            searchResults.nbHits
          );
        } else {
          trackProductListViewed(searchResults.hits, searchResults.nbHits);
        }
      }
    }, [searchResults, location]);

    if (
      mounted &&
      !limit &&
      showRefinements &&
      searchResults?.nbHits === 0 &&
      carryOverFilter === location?.search.replace(/query=[^&]+&?/, "")
    ) {
      console.log(
        "<==============REMOVE ALL FACET FILTER IF NO RESULT FOUND==============>"
      );
      const nextSearchState = {
        ...searchState,
        refinementList: null,
      };
      onSearchStateChange(nextSearchState);
    }

    return (
      <>
        {showRefinements && (
          <Media greaterThan="md">
            <CurrentFilter totalHit={searchResults?.nbHits || 0} />
          </Media>
        )}
        <StyledBox
          display="flex"
          justifyContent="space-between"
          flexDirection={["column", "column", "row"]}
        >
          <Configure
            hitsPerPage={limit || HITS_PER_PAGE}
            clickAnalytics={true}
            filters={combinedFilters}
            query={query}
            userToken={
              user
                ? process.env.GATSBY_VM_MARKET +
                  "-" +
                  user?.[`https://www.vinomofo.com/app_metadata`]?.[
                    `${process.env.GATSBY_VM_MARKET}_EXTERNAL_ID`
                  ]
                : 0
            }
          />

          {showRefinements && searchResults && searchResults.hits[0] && (
            <RefinementMenu totalHit={searchResults?.nbHits} />
          )}

          <Box flexGrow={1}>
            {isSearchStalled || (searching && searchResults?.nbHits === 0) ? (
              <Offers
                offers={skeletonList(limit || HITS_PER_PAGE)}
                columns={columns}
                skeleton={
                  isSearchStalled || (searching && searchResults?.nbHits === 0)
                }
                queryID={searchResults?.queryID}
              />
            ) : searchResults && searchResults.hits[0] ? (
              <>
                <Offers
                  offers={searchResults.hits}
                  columns={columns}
                  skeleton={isSearchStalled}
                  moreBtnLink={showMoreLink}
                  queryID={searchResults?.queryID}
                />
                {showPagination && <Pagination />}
              </>
            ) : null}
          </Box>
        </StyledBox>
      </>
    );
  }
);
